import { Box, CircularProgress } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useState } from "react"

import { CustomerPurchaseOrderRow } from "./CustomerPurchaseOrderRow"
import { CustomerPurchaseOrdersEmptyState } from "./CustomerPurchaseOrdersEmptyState"
import { CustomerPurchaseOrderFilterChips } from "./filters/CustomerPurchaseOrderFilterChips"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import SearchInput from "@/components/SearchInput/SearchInput"
import { type CustomerPurchaseOrderListItem, CustomerPurchaseOrderStatus } from "@/graphql/codegen/graphql"
import { useNavigationConfirmation } from "@/hooks/useNavigationConfirmation"
import { AddCustomerPurchaseOrderDialog } from "@/screens/CustomerOrders/components/AddCustomerPurchaseOrderDialog/AddCustomerPurchaseOrderDialog"
import { StatusDateRangeFilterDrawer } from "@/screens/CustomerOrders/components/filters/StatusDateRangeFilterDrawer"
import {
  CUSTOMER_PURCHASE_ORDER_DATE_RANGE_TYPE_OPTIONS,
  CUSTOMER_PURCHASE_ORDER_STATUS_OPTIONS,
} from "@/screens/CustomerOrders/constants"
import { useCustomerPurchaseOrderList } from "@/screens/CustomerOrders/hooks/useCustomerPurchaseOrderList"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"

export const CustomerPurchaseOrdersTab: FC = () => {
  const { companyId, shipToLocationId } = useParams({
    from: "/app/orders/$companyId/$shipToLocationId/_layout/customer-purchase-orders",
  })

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [deletingCpo, setDeletingCpo] = useState<CustomerPurchaseOrderListItem | undefined>()
  const [dirtyCpoIds, setDirtyCpoIds] = useState<Set<string>>(new Set())

  const { canCreate, isReadonly } = useSalesOrderPermissions()

  const {
    cpos,
    isFetching,
    searchParams: listSearchParams,
    tableContainerRef,
    handleScroll,
    handleSearchChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
    handleDeleteCpo,
    refetch,
  } = useCustomerPurchaseOrderList()

  const setCpoDirty = useCallback((cpoId: string, isDirty: boolean) => {
    setDirtyCpoIds((prevSet) => {
      const newSet = new Set(prevSet)
      if (isDirty) {
        newSet.add(cpoId)
      } else {
        newSet.delete(cpoId)
      }
      return newSet
    })
  }, [])

  const { isNavigationBlocked, handleNavigationConfirm, handleNavigationCancel } = useNavigationConfirmation({
    shouldBlock: dirtyCpoIds.size > 0,
  })

  const hasActiveFilters =
    (listSearchParams.statuses?.length ?? 0) > 0 || !!listSearchParams.dateFrom || !!listSearchParams.dateTo

  const handleDeleteConfirm = async () => {
    if (!deletingCpo) return
    await handleDeleteCpo(deletingCpo.customerPurchaseOrderId)
    setDeletingCpo(undefined)
  }

  const handleDuplicateSelect = (cpoNumber: string) => {
    setIsAddDialogOpen(false)
    handleSearchChange(cpoNumber)
  }

  return (
    <Box className='flex flex-col gap-4'>
      <Box className='flex items-center justify-between gap-4'>
        <Box className='flex flex-1 items-center gap-4'>
          <SearchInput
            placeholder='Search by CPO, SO, Customer Code, Product ID'
            value={listSearchParams.query}
            onChange={(e) => handleSearchChange(e.target.value)}
            className='w-full max-w-[524px]'
            data-testid='customer-orders.cpo-tab.search-input'
          />
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setIsFilterOpen(true)}
            data-testid='customer-orders.cpo-tab.filter-button'
          >
            Filter
          </Button>
        </Box>
        <Button
          appearance='contained'
          startIcon={<AddOutlined />}
          disabled={isReadonly}
          onClick={() => setIsAddDialogOpen(true)}
          data-testid='customer-orders.cpo-tab.add-cpo-button'
        >
          Customer Purchase Order
        </Button>
      </Box>

      <CustomerPurchaseOrderFilterChips searchParams={listSearchParams} onRemove={handleFilterRemove} />

      {isFetching && !cpos.length && (
        <Box className='flex items-center justify-center p-8'>
          <CircularProgress />
        </Box>
      )}

      {!isFetching && !cpos.length && (
        <CustomerPurchaseOrdersEmptyState hasActiveSearch={!!listSearchParams.query?.trim() || hasActiveFilters} />
      )}

      {cpos.length > 0 && (
        <Box ref={tableContainerRef} onScroll={handleScroll} className='flex max-h-full flex-col gap-4 overflow-y-auto'>
          {cpos.map((cpo) => (
            <CustomerPurchaseOrderRow
              key={cpo.customerPurchaseOrderId}
              cpo={cpo}
              companyId={companyId}
              shipToLocationId={shipToLocationId}
              canEdit={canCreate}
              canAddProducts={canCreate && cpo.status !== CustomerPurchaseOrderStatus.Complete}
              onDirtyChange={setCpoDirty}
              onDelete={setDeletingCpo}
            />
          ))}
        </Box>
      )}

      {isAddDialogOpen && (
        <AddCustomerPurchaseOrderDialog
          companyId={companyId}
          shipToLocationId={shipToLocationId}
          onClose={() => setIsAddDialogOpen(false)}
          onSubmit={refetch}
          onDuplicateSelect={handleDuplicateSelect}
        />
      )}

      {isNavigationBlocked && (
        <CancelDialog
          title='Unsaved changes'
          message='You have unsaved changes to the following items. If you leave this page, these changes will be lost.'
          closeLabel='Cancel'
          confirmLabel='Leave Page'
          onClose={handleNavigationCancel}
          onConfirm={handleNavigationConfirm}
        />
      )}

      {deletingCpo && (
        <ConfirmationDialog
          title='Are you sure you want to delete this CPO?'
          content='Deleting this CPO will remove all added products and details. You will need to add them again if you recreate the CPO.'
          closeLabel='Cancel'
          confirmLabel='Delete'
          isWarning
          onClose={() => setDeletingCpo(undefined)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <StatusDateRangeFilterDrawer
        open={isFilterOpen}
        searchParams={listSearchParams}
        onToggle={() => setIsFilterOpen((prev) => !prev)}
        onChange={handleFiltersChange}
        onClear={handleFilterClear}
        statusOptions={CUSTOMER_PURCHASE_ORDER_STATUS_OPTIONS}
        dateRangeTypeOptions={CUSTOMER_PURCHASE_ORDER_DATE_RANGE_TYPE_OPTIONS}
        testIdPrefix='customer-orders.cpo-filter-drawer'
      />
    </Box>
  )
}
